import { useState, useEffect } from 'react';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../../../components/ui/select';
import { toast } from 'sonner@2.0.3';
import { fontsApi } from '../../../shared/api/fonts.api';
import { buildFontOptions } from '../lib/buildFontOptions';
import { fontDisplayName } from '../lib/fontDisplayName';
import { resolveFontUpload } from '../lib/resolveFontUpload';
import { fontManager } from '../../../shared/lib/fontManager';
import { parseVariableFontAxes, type FontAxis } from '../../../shared/lib/variableFontUtils';

interface Font {
  name: string;
  url: string;
}

interface TemplateFontSelectorProps {
  selectedFontUrl: string;
  onFontSelect: (fontUrl: string, fontName: string, fontFamily: string) => void;
  
  onFontFileUpload: (
    file: File,
    fontFamily: string,
    fontPreviewUrl: string,
    displayName: string,
    selectUrl: string
  ) => void;
  
  /** Callback when variable font axes are detected */
  onAxesDetected?: (axes: FontAxis[]) => void;
  
  initialFontUrl?: string;
  initialFontName?: string;
}

/**
 * Компонент для выбора шрифта из списка или загрузки нового
 */
export function TemplateFontSelector({
  selectedFontUrl,
  onFontSelect,
  onFontFileUpload,
  onAxesDetected,
  initialFontUrl,
  initialFontName,
}: TemplateFontSelectorProps) {
  // Сразу показываем текущий шрифт (как поле лого показывает текущий логотип),
  // даже до загрузки списка с сервера.
  const [availableFonts, setAvailableFonts] = useState<Font[]>(() =>
    buildFontOptions([], { url: initialFontUrl, name: initialFontName })
  );
  const [fontPreview, setFontPreview] = useState<string>('');

  // Загрузка списка доступных шрифтов
  useEffect(() => {
    const current = { url: initialFontUrl, name: initialFontName };
    const loadFonts = async () => {
      try {
        const fonts = await fontsApi.list();
        setAvailableFonts(buildFontOptions(fonts, current));
      } catch (error) {
        console.error('Error loading fonts:', error);
        // Список не загрузился — всё равно показываем текущий шрифт.
        setAvailableFonts(buildFontOptions([], current));
      }
    };

    loadFonts();
  }, [initialFontUrl, initialFontName]);

  // Загружаем шрифт из initialData при монтировании И детектим оси variable font
  useEffect(() => {
    if (initialFontUrl && initialFontName) {
      // Загружаем шрифт через FontManager
      fontManager.loadFont(initialFontUrl, initialFontName).catch(error => {
        console.error('❌ Ошибка загрузки начального шрифта:', error);
      });

      // Автоматически детектим оси variable font при загрузке
      parseVariableFontAxes(initialFontUrl)
        .then(axes => {
          if (axes.length > 0) {
            console.log('🎛️ Variable font detected on init:', axes);
          }
          onAxesDetected?.(axes);
        })
        .catch(err => {
          console.warn('⚠️ Не удалось определить оси шрифта при инициализации:', err);
          onAxesDetected?.([]);
        });
    }
  }, [initialFontUrl, initialFontName]);

  const handleFontSelect = async (fontUrl: string) => {
    if (!fontUrl || fontUrl === 'default') {
      onFontSelect('', '', 'Arial');
      onAxesDetected?.([]);
      return;
    }

    // Найти имя шрифта
    const selectedFont = availableFonts.find((f) => f.url === fontUrl);
    if (selectedFont) {
      // Используем FontManager для загрузки
      try {
        const loadedFont = await fontManager.loadFont(fontUrl, selectedFont.name);
        toast.success('Шрифт применен');
        onFontSelect(fontUrl, selectedFont.name, loadedFont.fontFamily);
        
        // Детектим оси variable font
        try {
          const axes = await parseVariableFontAxes(fontUrl);
          if (axes.length > 0) {
            console.log('🎛️ Variable font detected:', axes);
            toast.success(`Variable font! ${axes.length} ${axes.length === 1 ? 'ось' : 'осей'} найдено`);
          }
          onAxesDetected?.(axes);
        } catch (axesError) {
          console.warn('⚠️ Не удалось определить оси шрифта:', axesError);
          onAxesDetected?.([]);
        }
      } catch (error) {
        console.error('Font load error:', error);
        toast.error('Ошибка загрузки шрифта');
        onFontSelect('', '', 'Arial');
        onAxesDetected?.([]);
      }
    }
  };

  const handleFontChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Проверка типа файла
    const validTypes = ['.ttf', '.otf', '.woff', '.woff2'];
    const fileExt = file.name.toLowerCase().slice(file.name.lastIndexOf('.'));
    if (!validTypes.includes(fileExt)) {
      toast.error('Пожалуйста, выберите шрифт (TTF, OTF, WOFF, WOFF2)');
      return;
    }

    // Если шрифт с таким именем уже в списке — не добавляем дубль, выбираем существующий.
    const resolution = resolveFontUpload(availableFonts, file.name);
    if (resolution.kind === 'existing') {
      toast.success(`Шрифт «${resolution.font.name}» уже в списке — выбран`);
      handleFontSelect(resolution.font.url);
      return;
    }

    // Детектим оси variable font из локального файла (быстро, без сети).
    parseVariableFontAxes(file).then(axes => {
      if (axes.length > 0) {
        console.log('🎛️ Variable font uploaded:', axes);
        toast.success(`Variable font! ${axes.length} ${axes.length === 1 ? 'ось' : 'осей'} найдено`);
      }
      onAxesDetected?.(axes);
    }).catch(() => {
      onAxesDetected?.([]);
    });

    // Локальный fallback (прежнее поведение): base64 в шаблон, без сохранения в список.
    const applyLocally = () => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const fontData = reader.result as string;
        setFontPreview(fontData);
        const customFontName = `CustomFont_${Date.now()}`;
        const fontFace = new FontFace(customFontName, `url(${fontData})`, {
          weight: '1 1000',
          style: 'normal',
          stretch: '25% 200%',
        });
        fontFace.load().then((loadedFont) => {
          document.fonts.add(loadedFont);
          const displayName = fontDisplayName(file.name);
          setAvailableFonts((prev) => buildFontOptions(prev, { url: fontData, name: displayName }));
          onFontFileUpload(file, customFontName, fontData, displayName, fontData);
        }).catch((error) => {
          console.error('Font load error:', error);
          toast.error('Ошибка загрузки шрифта');
        });
      };
      reader.readAsDataURL(file);
    };

    // Основной путь: сохраняем шрифт на сервер → он попадёт в общий список /fonts.
    (async () => {
      try {
        const { name, url } = await fontsApi.upload(file);
        const displayName = fontDisplayName(file.name);
        const loaded = await fontManager.loadFont(url, name);
        setFontPreview(url);
        toast.success('Шрифт сохранён');
        setAvailableFonts((prev) => buildFontOptions(prev, { url, name: displayName }));
        onFontFileUpload(file, loaded.fontFamily, url, displayName, url);
      } catch (err) {
        console.error('Font upload failed, applying locally:', err);
        toast.error('Не удалось сохранить шрифт на сервер — применён локально');
        applyLocally();
      }
    })();
  };

  return (
    <div className="space-y-2">
      <Label>Шрифт (опционально)</Label>

      {/* Селектор из загруженных шрифтов */}
      {availableFonts.length > 0 && (
        <Select value={selectedFontUrl} onValueChange={handleFontSelect}>
          <SelectTrigger>
            <SelectValue 
              placeholder={
                selectedFontUrl && initialFontName && initialFontName !== 'Arial' 
                  ? initialFontName 
                  : "Выберите из загруженных"
              } 
            />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="default">По умолчанию (Arial)</SelectItem>
            {availableFonts.map((font) => (
              <SelectItem key={font.url} value={font.url}>
                {font.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {/* Разделитель */}
      {availableFonts.length > 0 && (
        <div className="flex items-center gap-2">
          <div className="h-px bg-border flex-1" />
          <span className="text-xs text-muted-foreground">или загрузите новый</span>
          <div className="h-px bg-border flex-1" />
        </div>
      )}

      {/* Загрузка нового шрифта */}
      <div className="flex items-center gap-4">
        <Input
          type="file"
          accept=".ttf, .otf, .woff, .woff2"
          onChange={handleFontChange}
          id="fontUpload"
          className="flex-1"
        />
        {fontPreview && (
          <p className="text-sm text-muted-foreground">Шрифт загружен для превью</p>
        )}
      </div>
      <p className="text-xs text-muted-foreground">
        Загрузите файл шрифта (TTF, OTF, WOFF, WOFF2)
      </p>
    </div>
  );
}